import {
  GetPresenceStatusResponse,
  UserPresence_UserStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone/presence/v1/api.pb';
import { DeviceStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/presence/message.pb';
import dayjs from 'dayjs';
import { CardData, CardFilters, DEVICE_STATE } from './types';

// For later
// const SOFTPHONE_TYPE = DeviceType_Enum.SOFTPHONE;
const SOFTPHONE_TYPE = 'softphone';

const getDerivedUserStatuses = (item: Extract<CardData, { type: 'user' }>) => {
  const statuses = new Set<CardFilters['userStatus'][number]>([item.isOnline ? 'online' : 'offline']);

  if (item.deviceState === DEVICE_STATE.DISCONNECTED) {
    statuses.add(DEVICE_STATE.DISCONNECTED);
  }

  return statuses;
};

/**
 * Applies all table filters and sorting to the card list.
 */
export const filterAndSortCardData = (data: CardData[], filters: CardFilters) => {
  if (!data?.length) return [];

  const hasLocationFilter = filters.location.length > 0;
  const hasDeviceStateFilter = filters.deviceState.length > 0;
  const hasCardTypeFilter = filters.cardType.length > 0;
  const hasUserStatusFilter = filters.userStatus.length > 0;
  const searchLower = filters.search?.trim().toLowerCase() || '';
  const hasSearchFilter = Boolean(searchLower);

  const locationSet = hasLocationFilter ? new Set(filters.location) : null;
  const deviceStateSet = hasDeviceStateFilter ? new Set(filters.deviceState) : null;
  const cardTypeSet = hasCardTypeFilter ? new Set(filters.cardType) : null;
  const userStatusSet = hasUserStatusFilter ? new Set(filters.userStatus) : null;

  const filtered = data.filter((item) => {
    const matchesLocation = !hasLocationFilter || item.locations.some((loc) => locationSet?.has(loc.id));
    const matchesDeviceState =
      !hasDeviceStateFilter ||
      // Show ringing devices only if the user has selected "available" in the filter
      (deviceStateSet?.has(DEVICE_STATE.AVAILABLE) && item.deviceState === DEVICE_STATE.RINGING) ||
      !!deviceStateSet?.has(item.deviceState);
    const matchesCardType = !hasCardTypeFilter || !!cardTypeSet?.has(item.type);
    const matchesUserStatus =
      !hasUserStatusFilter ||
      item.type !== 'user' ||
      [...getDerivedUserStatuses(item)].some((status) => userStatusSet?.has(status));

    const matchesSearch =
      !hasSearchFilter ||
      item.name.toLowerCase().includes(searchLower) ||
      (item.type === 'device' && item.subtitle.toLowerCase().includes(searchLower));

    return matchesLocation && matchesDeviceState && matchesCardType && matchesUserStatus && matchesSearch;
  });

  if (filters.sortBy === 'ascending' || filters.sortBy === 'descending') {
    const direction = filters.sortBy === 'ascending' ? 1 : -1;
    return filtered.sort((a, b) => direction * a.name.localeCompare(b.name));
  }

  if (filters.sortBy === 'recent') {
    return filtered.sort((a, b) => dayjs(b.addedAt).diff(dayjs(a.addedAt)));
  }

  return filtered;
};

/**
 * Creates a stable identity signature for currently visible cards.
 * Used to avoid re-subscribing when only card metadata changes.
 */
export const getPresenceSubscriptionSignature = (cards: CardData[]) =>
  cards
    .map((item) => `${item.type}:${item.id}`)
    .sort()
    .join(',');

/**
 * Builds the backend subscription payload from currently visible cards.
 */
export const buildPresenceSubscriptionPayload = (cards: CardData[]) =>
  cards.reduce(
    (acc, item) => {
      if (item.type === 'user') {
        acc.userIds.push(item.id);
      } else {
        acc.sipProfileIds.push(item.id);
      }
      return acc;
    },
    { sipProfileIds: [], userIds: [] } as { sipProfileIds: string[]; userIds: string[] }
  );

/**
 * Applies softphone-specific user-card override rules based on user status changes.
 */
export const getSoftphoneUserCardDeviceStatusOverride = ({
  newUserStatus,
  previousUserStatus,
}: {
  newUserStatus: UserPresence_UserStatus;
  previousUserStatus: UserPresence_UserStatus;
}): DeviceStatus | undefined => {
  if (newUserStatus === UserPresence_UserStatus.USER_PRESENCE_OFFLINE) {
    return DeviceStatus.DEVICE_STATUS_OFFLINE;
  }

  if (
    newUserStatus === UserPresence_UserStatus.USER_PRESENCE_ONLINE &&
    previousUserStatus === UserPresence_UserStatus.USER_PRESENCE_OFFLINE
  ) {
    return DeviceStatus.DEVICE_STATUS_AVAILABLE;
  }

  return undefined;
};

/**
 * Applies softphone-specific device-card override rules based on linked user status.
 */
export const getSoftphoneDeviceCardDeviceStatusOverride = ({
  hasLinkedUser,
  newUserStatus,
  previousUserStatus,
}: {
  hasLinkedUser: boolean;
  newUserStatus: UserPresence_UserStatus;
  previousUserStatus: UserPresence_UserStatus;
}): DeviceStatus | undefined => {
  if (!hasLinkedUser || newUserStatus === UserPresence_UserStatus.USER_PRESENCE_OFFLINE) {
    return DeviceStatus.DEVICE_STATUS_OFFLINE;
  }

  if (
    newUserStatus === UserPresence_UserStatus.USER_PRESENCE_ONLINE &&
    previousUserStatus === UserPresence_UserStatus.USER_PRESENCE_OFFLINE
  ) {
    return DeviceStatus.DEVICE_STATUS_AVAILABLE;
  }

  return undefined;
};

/**
 * Reconciles merged presence snapshots with user-presence updates from the userpresence store.
 * This function is pure and is used as the canonical reconciliation step before card mapping.
 */
export const reconcilePresenceWithUserStatusUpdates = ({
  mergedPresenceData,
  userPresenceData,
}: {
  mergedPresenceData: {
    userPresence: GetPresenceStatusResponse['userPresence'];
    devicePresence: GetPresenceStatusResponse['devicePresence'];
  };
  userPresenceData: Record<string, { userStatus: string; lastSeen: string }>;
}) => {
  const updatedUserPresence = mergedPresenceData?.userPresence?.map((user) => {
    const userPresenceUpdate = userPresenceData[user.userPresence?.userId || ''];

    if (!userPresenceUpdate || !user.userPresence) {
      return user;
    }

    const newUserStatus =
      userPresenceUpdate.userStatus === 'ONLINE'
        ? UserPresence_UserStatus.USER_PRESENCE_ONLINE
        : UserPresence_UserStatus.USER_PRESENCE_OFFLINE;

    let newDevicePresence = user.devicePresence;

    /**
     * TODO: We should just update the deviceStatus directly from the devicePresenceUpdate.
     *
     * This is a workaround for softphones where the deviceStatus should be OFFLINE if the user is offline.
     * We would typically rely on registration events for this, but those are not currently available.
     */
    if (user.devicePresence?.deviceType === SOFTPHONE_TYPE && user.devicePresence) {
      const statusOverride = getSoftphoneUserCardDeviceStatusOverride({
        newUserStatus,
        previousUserStatus: user.userPresence.userStatus,
      });

      if (statusOverride) {
        newDevicePresence = {
          ...user.devicePresence,
          deviceStatus: statusOverride,
        };
      }
    }

    return {
      ...user,
      userPresence: {
        ...user.userPresence,
        userStatus: newUserStatus,
        lastSeen: userPresenceUpdate.lastSeen,
      },
      devicePresence: newDevicePresence,
    };
  });

  const updatedDevicePresence = mergedPresenceData?.devicePresence?.map((device) => {
    const userPresenceUpdate = userPresenceData[device.userPresence?.userId || ''];
    const previousUserStatus = device.userPresence?.userStatus ?? UserPresence_UserStatus.USER_PRESENCE_OFFLINE;

    const newUserStatus =
      userPresenceUpdate?.userStatus === undefined
        ? previousUserStatus
        : userPresenceUpdate.userStatus === 'ONLINE'
        ? UserPresence_UserStatus.USER_PRESENCE_ONLINE
        : UserPresence_UserStatus.USER_PRESENCE_OFFLINE;

    let newDevicePresence = device.devicePresence;

    /**
     * TODO: We should just update the deviceStatus directly from the devicePresenceUpdate.
     *
     * This is a workaround for softphones where the deviceStatus should be OFFLINE if the user is offline.
     * We would typically rely on registration events for this, but those are not currently available.
     */
    if (device.devicePresence?.deviceType === SOFTPHONE_TYPE && device.devicePresence) {
      const statusOverride = getSoftphoneDeviceCardDeviceStatusOverride({
        hasLinkedUser: !!device.userPresence,
        newUserStatus,
        previousUserStatus,
      });

      if (statusOverride) {
        newDevicePresence = {
          ...device.devicePresence,
          deviceStatus: statusOverride,
        };
      }
    }

    return {
      ...device,
      userPresence: device.userPresence
        ? {
            ...device.userPresence,
            userStatus: newUserStatus,
            lastSeen: userPresenceUpdate?.lastSeen ?? device.userPresence.lastSeen,
          }
        : undefined,
      devicePresence: newDevicePresence,
    };
  });

  return {
    updatedUserPresence,
    updatedDevicePresence,
  };
};
